import { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { arrayMove } from '@dnd-kit/sortable'

import { ToastContext, useFetch } from '@reveni-io/reveni-ui'

import { baseReasons } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useReturnPortalFeatures from '@/common/hooks/useReturnPortalFeatures/useReturnPortalFeatures'
import useStoreProductCategories from '@/common/hooks/useStoreProductCategories/useStoreProductCategories'
import { focusOnError, getTranslations } from '@/configuration/pages/ConfigurationPage/helpers'
import { actionTypeValues } from '@/configuration/pages/ConfigurationPage/ReturnReasonsConfiguration/ReturnReasonDetailConfiguration/ActionsSection/constants'
import { notesAndImageValidationValues } from '@/configuration/pages/ConfigurationPage/ReturnReasonsConfiguration/ReturnReasonDetailConfiguration/constants'
import { availabilityModes } from '@/configuration/pages/ConfigurationPage/ReturnReasonsConfiguration/ReturnReasonDetailConfiguration/constants'
import configurationServices from '@/configuration/services/services'
import routes from '@/Router/routes.constants'

import {
  getCreateReturnReasonPayload,
  getEditReturnReasonPayload,
  getNewSubReasonObject,
  getSelectedCategories,
  getSelectedProductTypes,
} from './helpers'

const useReturnReason = ({ id, storeId, selectedReturnReason, returnPortalId }) => {
  const { t } = useTranslation()
  const reasons = baseReasons(t)
  const navigate = useNavigate()
  const location = useLocation()
  const { info } = useContext(AppContext)
  const supportedLanguages = info?.selectedStore?.supported_languages
  const { setToastInfo } = useContext(ToastContext)
  const [selectedType, setSelectedType] = useState(reasons[0]?.value)
  const [customTitles, setCustomTitles] = useState(null)
  const [customMessages, setCustomMessages] = useState(null)
  const [notesValidation, setNotesValidation] = useState(notesAndImageValidationValues.required)
  const [imageValidation, setImageValidation] = useState(notesAndImageValidationValues.required)
  const [useCustomTitle, setUseCustomTitle] = useState(false)
  const [transactionTypes, setTransactionTypes] = useState([])
  const [selectedCategories, setSelectedCategories] = useState([])
  const [selectedProductTypes, setSelectedProductTypes] = useState([])
  const [actionForTransactionTypes, setActionForTransactionTypes] = useState([])
  const [useCustomMessage, setUseCustomMessage] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [returnReasonToEdit, setReturnReasonToEdit] = useState(null)
  const [error, setError] = useState('')
  const [subReasons, setSubReasons] = useState([])
  const [action, setAction] = useState(actionTypeValues.none)
  const [bonusTypes, setBonusTypes] = useState([])
  const [tags, setTags] = useState([])
  const [matchCase, setMatchCase] = useState(false)
  const [availabilityMode, setAvailabilityMode] = useState(availabilityModes.all)

  const { productCategories, fetchingProductCategories } = useStoreProductCategories({
    storeId,
    doInitialRequest: true,
  })
  const { data: productTypes, loading: fetchingProductTypes } = useFetch(
    () => configurationServices.getStoreProductTypes({ storeId }),
    {
      doInitialRequest: true,
    }
  )

  const { fetchData: createReturnReason } = useFetch(configurationServices.createPortalReturnReason, {
    doInitialRequest: false,
  })
  const { fetchData: editReturnReason } = useFetch(configurationServices.editPortalReturnReason, {
    doInitialRequest: false,
  })
  const { returnPortalFeatures, fetchingReturnPortalFeatures } = useReturnPortalFeatures({
    returnPortalId: info?.selectedReturnPortal?.id,
  })

  const setValuesFromReturnReason = useCallback(
    returnReason => {
      setReturnReasonToEdit(returnReason)
      setCustomTitles(getTranslations(supportedLanguages, returnReason?.display))
      setSelectedType(returnReason?.key)
      setNotesValidation(returnReason?.notes_validation || notesAndImageValidationValues.required)
      setImageValidation(returnReason?.image_validation || notesAndImageValidationValues.required)
      setUseCustomTitle(Object.values(returnReason?.display).some(value => value !== null))
      setUseCustomMessage(Object.values(returnReason?.custom_message).some(value => value !== null))
      setTransactionTypes(returnReason?.transaction_types)
      setSelectedCategories(getSelectedCategories(returnReason))
      setSelectedProductTypes(getSelectedProductTypes(productTypes, returnReason))
      setActionForTransactionTypes(returnReason?.action_for_transaction_types)
      setCustomMessages(getTranslations(supportedLanguages, returnReason?.custom_message))
      setSubReasons(returnReason?.subreasons || [])
      setAction(returnReason?.action || actionTypeValues.none)
      setBonusTypes(returnReason?.bonus_types || [])
      setTags(returnReason?.product_tags || [])
      setMatchCase(returnReason?.product_tags_case_sensitive || false)
      setAvailabilityMode(returnReason?.availability_mode || availabilityModes.all)
    },
    [productTypes, supportedLanguages]
  )

  useEffect(() => {
    if (id !== 'new') {
      const returnReason = selectedReturnReason
      if (returnReason && !returnReasonToEdit && productCategories && productTypes)
        setValuesFromReturnReason(returnReason)
    }
  }, [id, productCategories, productTypes, returnReasonToEdit, setValuesFromReturnReason, selectedReturnReason])

  const onCreateReturnReason = async () => {
    try {
      const payload = getCreateReturnReasonPayload({
        selectedType,
        useCustomTitle,
        customTitles,
        actionForTransactionTypes,
        customMessages,
        notesValidation,
        imageValidation,
        selectedCategories,
        transactionTypes,
        useCustomMessage,
        selectedProductTypes,
        subReasons,
        returnReasonsFiltering: returnPortalFeatures?.return_reasons_filtering,
        availabilityMode,
        action,
        bonusTypes,
        tags,
        matchCase,
      })
      await createReturnReason({ returnPortalId, returnReason: payload })
      setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
      navigate(routes.configurationPage.returnReasons)
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })

      focusOnError(error?.response?.data, setError)
    }
  }

  const onEditReturnReason = async () => {
    try {
      const payload = getEditReturnReasonPayload({
        actionForTransactionTypes,
        customMessages,
        customTitles,
        notesValidation,
        imageValidation,
        returnReasonToEdit,
        selectedCategories,
        selectedType,
        transactionTypes,
        useCustomMessage,
        useCustomTitle,
        supportedLanguages,
        selectedProductTypes,
        subReasons,
        returnReasonsFiltering: returnPortalFeatures?.return_reasons_filtering,
        availabilityMode,
        action,
        bonusTypes,
        tags,
        matchCase,
      })
      await editReturnReason({ returnPortalId, returnReason: payload })
      setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
      setHasChanges(false)
      navigate(`${routes.configurationPage.returnReasons}/${id}`, {
        state: { ...location.state, selectedReturnReason: payload },
      })
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const revertChanges = () => {
    if (id === 'new') {
      setSelectedType(reasons[0]?.value)
      setCustomTitles(null)
      setCustomMessages(null)
      setNotesValidation(notesAndImageValidationValues.required)
      setImageValidation(notesAndImageValidationValues.required)
      setUseCustomTitle(false)
      setTransactionTypes([])
      setSelectedCategories([])
      setActionForTransactionTypes([])
      setUseCustomMessage(false)
      setHasChanges(false)
      setError(false)
      setAction(actionTypeValues.none)
      setSubReasons([])
      setSelectedProductTypes([])
      setReturnReasonToEdit(null)
      setBonusTypes([])
      setTags([])
      setMatchCase(false)
      setAvailabilityMode(availabilityModes.all)
    } else {
      setValuesFromReturnReason(returnReasonToEdit)
      setHasChanges(false)
    }
  }

  const addSubReason = languages => {
    setSubReasons([...subReasons, getNewSubReasonObject(languages, subReasons)])
    setHasChanges(true)
  }

  const deleteSubReason = id => {
    setSubReasons(subReasons.filter(subReason => subReason.id !== id))
    setHasChanges(true)
  }

  const editSubReasonInternalName = (newInternalName, id) => {
    setSubReasons(
      subReasons.map(subReason => {
        if (subReason.id === id) {
          return { ...subReason, internal_name: newInternalName, key: newInternalName.replace(/ /g, '_').toLowerCase() }
        }
        return subReason
      })
    )
    setHasChanges(true)
  }

  const editSubReasonDisplay = (newDisplay, id, lang) => {
    setSubReasons(
      subReasons.map(subReason => {
        if (subReason.id === id) {
          return { ...subReason, display: { ...subReason.display, [lang]: newDisplay } }
        }
        return subReason
      })
    )
    setHasChanges(true)
  }

  const reorderSubReasons = event => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = subReasons.findIndex(sr => sr.id === active.id)
    const newIndex = subReasons.findIndex(sr => sr.id === over.id)

    setSubReasons(arrayMove(subReasons, oldIndex, newIndex))
    setHasChanges(true)
  }

  const allSubReasonsAreValid = () => {
    return subReasons.every(
      subReason => subReason.internal_name && supportedLanguages.every(language => subReason.display[language])
    )
  }

  const onToggleNotesValidation = checked => {
    setNotesValidation(checked ? notesAndImageValidationValues.required : notesAndImageValidationValues.disabled)
    setHasChanges(true)
  }

  const onToggleImageValidation = checked => {
    setImageValidation(checked ? notesAndImageValidationValues.required : notesAndImageValidationValues.disabled)
    setHasChanges(true)
  }

  const onChangeNotesValidation = value => {
    setNotesValidation(value)
    setHasChanges(true)
  }

  const onChangeImageValidation = value => {
    setImageValidation(value)
    setHasChanges(true)
  }

  const onChangeAction = actionType => {
    if (actionType === actionTypeValues.block) {
      setBonusTypes([])
    } else if (actionType === actionTypeValues.bonus) {
      setActionForTransactionTypes([])
    } else {
      setBonusTypes([])
      setActionForTransactionTypes([])
    }

    setAction(actionType)
    setHasChanges(true)
  }

  return {
    productCategories,
    hasChanges,
    revertChanges,
    fetchingProductCategories,
    selectedType,
    setSelectedType: params => {
      setSelectedType(params)
      setHasChanges(true)
    },
    customTitles,
    setCustomTitles: params => {
      setCustomTitles(params)
      setHasChanges(true)
    },
    customMessages,
    setCustomMessages: params => {
      setCustomMessages(params)
      setHasChanges(true)
    },
    notesValidation,
    imageValidation,
    onToggleNotesValidation,
    onToggleImageValidation,
    onChangeNotesValidation,
    onChangeImageValidation,
    useCustomTitle,
    setUseCustomTitle: params => {
      setUseCustomTitle(params)
      setHasChanges(true)
    },
    transactionTypes,
    setTransactionTypes: params => {
      setTransactionTypes(params)
      setHasChanges(true)
    },
    selectedCategories,
    setSelectedCategories: params => {
      setSelectedCategories(params)
      setHasChanges(true)
    },
    actionForTransactionTypes,
    setActionForTransactionTypes: params => {
      setActionForTransactionTypes(params)
      setHasChanges(true)
    },
    useCustomMessage,
    setUseCustomMessage: params => {
      setUseCustomMessage(params)
      setHasChanges(true)
    },
    reasons,
    onCreateReturnReason,
    onEditReturnReason,
    returnReasonToEdit,
    productTypes,
    selectedProductTypes,
    setSelectedProductTypes: params => {
      setSelectedProductTypes(params)
      setHasChanges(true)
    },
    fetchingProductTypes,
    error,
    subReasons,
    addSubReason,
    setSubReasons,
    deleteSubReason,
    editSubReasonInternalName,
    editSubReasonDisplay,
    reorderSubReasons,
    allSubReasonsAreValid,
    returnPortalFeatures,
    fetchingReturnPortalFeatures,
    action,
    onChangeAction,
    bonusTypes,
    setBonusTypes: params => {
      setBonusTypes(params)
      setHasChanges(true)
    },
    tags,
    setTags: params => {
      setTags(params)
      setHasChanges(true)
    },
    matchCase,
    setMatchCase: params => {
      setMatchCase(params)
      setHasChanges(true)
    },
    availabilityMode,
    setAvailabilityMode: params => {
      setAvailabilityMode(params)
      setHasChanges(true)
    },
  }
}

export default useReturnReason
